import { ReactNode } from 'react';

// ── SectionHeaderAction ───────────────────────────────────────────────────────
// THE canonical button for DetailSection titleRight slots.
// Default visual: cyan-outline, 24px height, 11px semibold — matches all
// existing section header buttons. Use tone="amber" only for warning actions
// (Pause, Danger); tone="muted" only for secondary compositor actions.
// ─────────────────────────────────────────────────────────────────────────────

type SectionHeaderActionTone = 'cyan-outline' | 'cyan' | 'amber' | 'muted';

interface SectionHeaderActionProps {
  onClick?: () => void;
  children: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  tone?: SectionHeaderActionTone;
}

const TONE_STYLES: Record<SectionHeaderActionTone, { border: string; color: string; bg: string }> = {
  'cyan-outline': { border: 'var(--evo-cyan)',   color: 'var(--evo-cyan)',  bg: 'transparent'        },
  'cyan':         { border: 'var(--evo-cyan)',   color: '#0a0a0a',          bg: 'var(--evo-cyan)'    },
  'amber':        { border: 'var(--evo-amber)',  color: 'var(--evo-amber)', bg: 'transparent'        },
  'muted':        { border: 'var(--evo-stroke)', color: 'var(--evo-fg-3)', bg: 'transparent'        },
};

export function SectionHeaderAction({
  onClick,
  children,
  icon,
  disabled,
  loading,
  loadingLabel,
  tone = 'cyan-outline',
}: SectionHeaderActionProps) {
  const c = TONE_STYLES[tone];
  const isDisabled = disabled || loading;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      style={{
        height: '24px',
        padding: '0 10px',
        borderRadius: '4px',
        flexShrink: 0,
        border: `1px solid ${c.border}`,
        background: c.bg,
        color: c.color,
        fontFamily: 'var(--evo-font)',
        fontSize: '11px',
        fontWeight: 600,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        transition: 'opacity 120ms',
        display: 'flex',
        alignItems: 'center',
        gap: icon ? '6px' : undefined,
      }}
    >
      {icon}
      {loading ? (loadingLabel ?? children) : children}
    </button>
  );
}
